import Decimal, {DecimalSource} from './break_infinity';
// import {cached} from './decorators/cached';
import {Shape} from './interfaces/shape';
import {p5} from './runner-idle';

type VectorString = `<${string}, ${string}, ${string}>`;
export class Vector implements Shape {
  // #region Properties (16)

  public static readonly DISTANCE_CACHE_MAX_SIZE = 1000;
  public static readonly distanceCache: Map<
    [VectorString, VectorString],
    Decimal
  > = new Map();

  public static I = new Vector(1, 0, 0);
  public static J = new Vector(0, 1, 0);
  public static K = new Vector(0, 0, 1);

  public x = new Decimal(0);
  public y = new Decimal(0);
  public z = new Decimal(0);

  public set 0(value) {
    this.x = value;
  }
  public get 0() {
    return this.x;
  }

  public set 1(value) {
    this.y = value;
  }
  public get 1() {
    return this.y;
  }

  public set 2(value) {
    this.z = value;
  }
  public get 2() {
    return this.z;
  }

  /** Direction as arctangent: 0 on +x, +ccw */
  public get direction(): Decimal {
    return new Decimal(Math.atan2(this.y.toNumber(), this.x.toNumber()));
  }

  public get magnitude(): Decimal {
    return this.x.sqr().plus(this.y.sqr()).plus(this.z.sqr()).sqrt();
  }

  public get normalized(): Vector {
    return this.div(this.magnitude);
  }

  // #endregion Properties (16)

  // #region Constructors (1)

  constructor(
    nx: DecimalSource = 0,
    ny: DecimalSource = 0,
    nz: DecimalSource = 0
  ) {
    this.x = new Decimal(nx);
    this.y = new Decimal(ny);
    this.z = new Decimal(nz);
  }

  // #endregion Constructors (1)

  // #region Public Static Methods (4)

  public static equals(a: Vector, b: Vector): boolean;
  public static equals(a: Vector): (b: Vector) => boolean;
  public static equals(a: Vector, b?: Vector) {
    if (b === undefined) return (b: Vector) => a.eq(b);
    return a.eq(b);
  }

  public static random(magnitude: DecimalSource = 1) {
    const direction = Math.random() * Math.PI * 2;
    return new Vector(
      new Decimal(magnitude).times(Math.cos(direction)),
      new Decimal(magnitude).times(Math.sin(direction))
    );
  }

  // #endregion Public Static Methods (4)

  // #region Public Methods (23)

  *[Symbol.iterator]() {
    yield this.x;
    yield this.y;
    yield this.z;
  }

  public abs(): Vector {
    return this.map(Decimal.abs);
  }

  public contains(point: Vector) {
    return this.equals(point);
  }

  public cross(otherVector: Vector): Vector {
    return new Vector(
      this.y.times(otherVector.z).minus(this.z.times(otherVector.y)),
      this.x.times(otherVector.z).minus(this.z.times(otherVector.x)),
      this.x.times(otherVector.y).minus(this.y.times(otherVector.x))
    );
  }

  /** MEMORY LEAK or rather, unlimited cache */
  public distance(otherVector: Vector): Decimal {
    const key: [VectorString, VectorString] = [
      this.toString(),
      otherVector.toString(),
    ];
    if (
      !Vector.distanceCache.has(key) &&
      !Vector.distanceCache.has([key[1], key[0]])
    ) {
      Vector.distanceCache.set(key, this.minus(otherVector).magnitude);
      if (Vector.distanceCache.size > Vector.DISTANCE_CACHE_MAX_SIZE) {
        Vector.distanceCache.delete([...Vector.distanceCache][0][0]);
      }
    }
    return Vector.distanceCache.get(key) ?? new Decimal(0);
  }

  public div(scalar: DecimalSource): Vector {
    return this.map((value: Decimal) => value.div(scalar));
  }

  public dot(otherVector: Vector): Decimal {
    return this.x
      .times(otherVector.x)
      .plus(this.y.times(otherVector.y))
      .plus(this.z.times(otherVector.z));
  }

  public draw(p: typeof p5, zoomIndex: Decimal) {
    // No default draw for Vectors? Make some purdy subtypes?
  }

  public eq(otherVector: Vector): boolean {
    return (
      this.x.eq(otherVector.x) &&
      this.y.eq(otherVector.y) &&
      this.z.eq(otherVector.z)
    );
  }

  public equals(otherVector: Vector): boolean {
    return this.eq(otherVector);
  }

  public map(transformer: (val: Decimal) => Decimal) {
    return new Vector(
      transformer(this.x),
      transformer(this.y),
      transformer(this.z)
    );
  }

  public minus(otherVector: Vector): Vector {
    return new Vector(
      this.x.minus(otherVector.x),
      this.y.minus(otherVector.y),
      this.z.minus(otherVector.z)
    );
  }

  public nearestPointTo(_point: Vector): Vector {
    return this;
  }

  public negate(): Vector {
    return this.map(Decimal.negate);
  }

  public notEquals(otherVector: Vector): boolean {
    return !this.eq(otherVector);
  }

  public plus(otherVector: Vector): Vector {
    return new Vector(
      this.x.plus(otherVector.x),
      this.y.plus(otherVector.y),
      this.z.plus(otherVector.z)
    );
  }

  public rotated(angle: number, center: Vector = new Vector()) {
    const postTranslate = this.minus(center);
    const postRotate = new Vector(
      postTranslate.x
        .times(Math.cos(angle))
        .plus(postTranslate.y.times(Math.sin(angle))),
      postTranslate.x
        .times(-Math.sin(angle))
        .plus(postTranslate.y.times(Math.cos(angle)))
    );
    const postRecenter = postRotate.plus(center);
    return postRecenter;
  }

  public hasTagsAt(point: Vector): boolean {
    return this.contains(point);
  }

  public times(scalar: DecimalSource): Vector {
    return this.map((value: Decimal) => value.times(scalar));
  }

  public toArray(): [Decimal, Decimal, Decimal] {
    return [this.x, this.y, this.z];
  }

  public toJSON(): [number, number, number] {
    return this.toArray().map((d) => d.toNumber()) as [number, number, number];
  }

  public toString(): VectorString {
    return `<${this.x.toString()}, ${this.y.toString()}, ${this.z.toString()}>`;
  }

  public withLength(length: Decimal): Vector {
    return this.normalized.times(length);
  }

  // #endregion Public Methods (23)
}
